import { routeGenerationMessage } from './planning/routeMessages';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { toast } from 'react-toastify';
import baseURL, { endPoints } from '../../Config';
import RouteCityMap, { buildRouteStops } from './RouteCityMap';
import LiveLocationPilot from './LiveLocationPilot';
import RouteOperationalSettings from './RouteOperationalSettings';
import RouteScheduleSummary from './RouteScheduleSummary';
import { Box, Alert, Button as MuiButton, Chip, Stack, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab, Autocomplete } from '@mui/material';
import MachineReviewTable from './planning/MachineReviewTable';
import RouteHistory from './planning/RouteHistory';
import RouteFollowUp from './planning/RouteFollowUp';
import SubmissionVerification from './planning/SubmissionVerification';
import PriorityRules from './planning/PriorityRules';
import RouteTemplate from './planning/RouteTemplate';
import RouteLogistics from './planning/RouteLogistics';
import GenerationProgress from './planning/GenerationProgress';
import RouteTabs, { preparationSteps, stepIndex } from './planning/RouteTabs';
import RouteReview from './planning/RouteReview';
import PlanningWorkspace from './planning/PlanningWorkspace';
import RouteOverview from './planning/RouteOverview';
import DailyDispatch from './planning/DailyDispatch';
import RepairServices from './planning/RepairServices';
import RouteFocusHeader, { StepNotices } from './planning/RouteFocusHeader';


const ACTIONS = ['Service & Collect', 'Service Only', 'Collect Only'];

function userContext() {
    const raw = localStorage.getItem('adminUser') || localStorage.getItem('userDetails') || '{}';
    const payload = JSON.parse(raw);
    return {
        token: payload?.data?.token || payload?.data?.accessToken,
        role: payload?.data?.adminUser?.role?.uid || '',
        user: payload?.data?.adminUser
    };
}

export function apiDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    return [String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0'), date.getFullYear()].join('/');
}

export function tomorrow(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Panama', year: 'numeric', month: 'numeric', day: 'numeric'
    }).formatToParts(now);
    const part = (type) => Number(parts.find((value) => value.type === type).value);
    // Calendar expects a local Date; its calendar components represent Panama's date.
    return new Date(part('year'), part('month') - 1, part('day') + 1, 12);
}

export function metricDisplay(value, suffix = '') {
    return value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
        ? 'Sin datos' : Number(value).toLocaleString('es-PA', { maximumFractionDigits: 2 }) + suffix;
}

export function sourceDateDisplay(value) {
    if (!value) return 'Sin datos';
    const routeDate = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value));
    if (routeDate) return routeDate[2] + '/' + routeDate[1] + '/' + routeDate[3];
    // Preserve timezone-less external timestamps exactly as reported, without treating them as UTC.
    const local = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}:\d{2}(?::\d{2})?))?$/.exec(String(value));
    if (local) return local[3] + '/' + local[2] + '/' + local[1] + (local[4] ? ' ' + local[4] + ' (hora informada)' : '');
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Sin datos' : new Intl.DateTimeFormat('es-PA', {
        timeZone: 'America/Panama', dateStyle: 'short', timeStyle: 'short'
    }).format(date);
}

export function daysSourceDisplay(item) {
    const source = item.source_context;
    if (!source) return 'Fuente no conservada en esta versión';
    if (source.days_reference === 'parlevel_planning_counter') return 'Contador informado por la planificación de Parlevel';
    if (source.days_reference === 'inventory_snapshot_counter') return 'Contador del catálogo sincronizado';
    return 'Sin fuente disponible';
}

export function itemChanged(item, before) {
    return !before || item.selected !== before.selected || item.action_name !== before.action_name ||
        (before?.requires_manual_review && item.review_decision !== undefined && item.review_decision !== (before.review_resolved ? before.review_resolution : 'pending')) || Boolean(item.edit_reason?.trim());
}

export function machineChanges(items, originalItems) {
    const original = new Map(originalItems.map((item) => [item.machine_id, item]));
    return items.filter((item) => itemChanged(item, original.get(item.machine_id))).map((item) => ({
        machine_id: item.machine_id, selected: item.selected, action_name: item.action_name,
        review_decision: item.review_decision, reason: (item.edit_reason || '').trim()
    }));
}

function statusLabel(status, proposalItems) {
    if (['draft', 'ready_for_review'].includes(status) && proposalItems && !proposalItems.some((item) => item.selected)) return 'Sin máquinas seleccionadas';
    const labels = {
        draft: 'Borrador',
        ready_for_review: 'Lista para revisar',
        submitting: 'Envío en curso / pendiente de verificación',
        submitted: 'Enviada',
        rejected: 'Rechazada',
        failed: 'Requiere verificación (registro anterior)'
    };
    return labels[status] || status;
}

export function machineTypeDisplay(item = {}) {
    return {
        type: item.machine_type || 'No disponible',
        model: item.make_model || null
    };
}

export function scoreBreakdownDisplay(item = {}) {
    const score = item.score_breakdown || {};
    return [
        { label: 'Inventario', value: score.inventory ?? null },
        { label: 'Días desde servicio', value: score.days_since_service ?? null },
        { label: 'Efectivo esperado', value: score.expected_cash ?? null },
        { label: 'Agotados', value: score.sold_outs ?? null },
        { label: 'Avería', value: score.breakdown ?? null }
    ];
}

export function formatDurationMinutes(value) {
    if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return 'Sin datos';
    const totalMinutes = Math.max(0, Math.round(Number(value) || 0));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours + ' h ' + minutes + ' min';
}

export function routeEstimateDisplay(proposal = {}, items = []) {
    const optimization = proposal.route_optimization || {};
    const selected = items.filter((item) => item.selected);
    const assumptions = optimization.operational_assumptions || {};
    const minutesPerLocation = Number(assumptions.minutes_per_location ?? 5);
    const minutesPerMachine = Number(assumptions.minutes_per_machine ?? 10);
    const locations = new Set(selected.map((item) => String(item.location_id || '')).filter(Boolean));
    const orderedLocations = new Set(
        (optimization.ordered_locations || [])
            .map((location) => String(location.id || location.location_id || ''))
            .filter(Boolean)
    );
    const reliable = selected.filter((item) => !item.requires_manual_review).length;
    const validLocations = selected.filter((item) => {
        const latitude = Number(item.location?.lat ?? item.location?.latitude);
        const longitude = Number(item.location?.lng ?? item.location?.longitude);
        return item.location_id && Number.isFinite(latitude) && Number.isFinite(longitude);
    }).length;
    const coverageRatio = locations.size
        ? Array.from(locations).filter((id) => orderedLocations.has(id)).length / locations.size
        : 0;
    const minimum = Math.max(0, Number(proposal.min_machines || 0));
    const maximum = Math.max(minimum, Number(proposal.max_machines ?? selected.length));
    const components = {
        data_quality: selected.length ? Math.round((reliable / selected.length) * 40) : 0,
        route_coverage: Math.round(coverageRatio * 25),
        location_data: selected.length ? Math.round((validLocations / selected.length) * 15) : 0,
        capacity_fit: selected.length >= minimum && selected.length <= maximum && selected.length ? 20 : 0
    };
    const confidenceScore = selected.length
        ? Object.values(components).reduce((sum, value) => sum + value, 0)
        : null;
    const drivingSeconds = optimization.total_duration_seconds == null ? null : Math.max(0, Number(optimization.total_duration_seconds));
    const locationMinutes = locations.size * minutesPerLocation;
    const settings = proposal.operational_settings;
    const machineMinutes = selected.reduce((total, item) => {
        const rule = settings?.overrides?.find(candidate => candidate.machine_type.trim().toLowerCase() === String(item.machine_type || '').trim().toLowerCase() && candidate.action_name === item.action_name);
        return total + (rule?.minutes ?? minutesPerMachine);
    }, 0);
    const extraMinutes = (settings?.preparation_minutes || 0) + (settings?.closing_minutes || 0) + (settings?.break_minutes || 0);
    const operationalSeconds = drivingSeconds
        + locationMinutes * 60
        + machineMinutes * 60 + extraMinutes * 60 + (optimization.schedule?.waiting_seconds || 0);
    return {
        drivingMinutes: drivingSeconds === null ? null : Math.round(drivingSeconds / 60),
        locationMinutes,
        machineMinutes,
        extraMinutes,
        waitingMinutes: (optimization.schedule?.waiting_seconds || 0) / 60,
        operationalMinutes: drivingSeconds === null ? null : Math.round(operationalSeconds / 60),
        assumptions: {
            minutesPerLocation,
            minutesPerMachine,
            selectedLocations: locations.size,
            selectedMachines: selected.length
        },
        confidence: {
            score: confidenceScore,
            label: confidenceScore === null
                ? 'No disponible'
                : confidenceScore >= 85 ? 'Alta' : confidenceScore >= 70 ? 'Media' : 'Baja',
            components
        }
    };
}

function communicationAgeLabel(ageHours) {
    if (ageHours === null || ageHours === undefined || ageHours === '') return null;
    const hours = Number(ageHours);
    if (!Number.isFinite(hours) || hours < 0) return null;
    if (hours < 1) return Math.max(1, Math.round(hours * 60)) + ' min antes de generar';
    if (hours < 24) return Math.round(hours) + ' h antes de generar';
    const days = Math.floor(hours / 24);
    return days + (days === 1 ? ' día antes de generar' : ' días antes de generar');
}

export function communicationDisplay(item = {}) {
    const freshness = item.freshness || {};
    const rawDate = freshness.last_communication;
    const parsed = rawDate ? new Date(rawDate) : null;
    const validDate = parsed && !Number.isNaN(parsed.getTime());
    const reasons = Array.isArray(freshness.reasons) ? freshness.reasons : [];
    const reasonLabels = [];
    if (validDate && (freshness.offline || reasons.includes('offline'))) reasonLabels.push('Comunicación no vigente');
    if (reasons.includes('missing_last_communication') || !validDate) {
        reasonLabels.push('Sin datos de comunicación');
    } else if (reasons.includes('stale_last_communication')) {
        reasonLabels.push('Más de 24 horas');
    }
    return {
        exact: validDate
            ? (() => {
                const parts = new Intl.DateTimeFormat('en-US', {
                    timeZone: 'America/Panama',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                }).formatToParts(parsed);
                const value = (type) => parts.find((part) => part.type === type)?.value || '';
                const period = value('dayPeriod').toLowerCase() === 'pm' ? 'p. m.' : 'a. m.';
                return value('day') + '/' + value('month') + '/' + value('year')
                    + ', ' + value('hour') + ':' + value('minute') + ' ' + period;
            })()
            : 'Sin comunicación registrada',
        age: communicationAgeLabel(freshness.age_hours),
        reasons: Array.from(new Set(reasonLabels)).join(' · ')
    };
}

function unwrap(response) {
    return response?.data?.data ?? response?.data;
}

export default function ParlevelRoutePlanning() {
    const context = useMemo(userContext, []);
    const [capabilities, setCapabilities] = useState(null);
    const [dailySending, setDailySending] = useState(false);
    const [dailyPolicy, setDailyPolicy] = useState(() => {
        try { return { mode: 'daily', minimum_score: null, coffee_every_days: null, ...JSON.parse(localStorage.getItem('routeDailyPolicy:' + (context.user?._id || 'local')) || '{}') }; }
        catch { return { mode: 'daily', minimum_score: null, coffee_every_days: null }; }
    });
    const setDailyRule = (key, value) => setDailyPolicy(current => {
        const next = { ...current, [key]: value === '' ? null : Number(value) };
        localStorage.setItem('routeDailyPolicy:' + (context.user?._id || 'local'), JSON.stringify(next));
        return next;
    });
    const [availabilityCheck, setAvailabilityCheck] = useState(null);
    // Temporary policy matches the server: all authenticated users can plan routes.
    const defaultWrite = Boolean(context.token);
    const canWrite = capabilities?.prepare ?? defaultWrite;
    const canApprove = capabilities?.approve ?? defaultWrite;
    const canManageRules = capabilities?.manage_rules ?? defaultWrite;
    const [serviceDate, setServiceDate] = useState(tomorrow());
    const [routeOptions, setRouteOptions] = useState([]);
    const routeScopeKey = 'routePlanningScope:' + (context.user?._id || 'local');
    const [routeIds, updateRouteIds] = useState(() => {
        try { const ids = JSON.parse(localStorage.getItem(routeScopeKey) || '[]'); return Array.isArray(ids) && ids.every(id => Number.isSafeInteger(id) && id > 0) ? ids : []; }
        catch { return []; }
    });
    const setRouteIds = ids => { updateRouteIds(ids); localStorage.setItem(routeScopeKey, JSON.stringify(ids)); };
    const [generationResults, setGenerationResults] = useState([]);
    const [generationJob, setGenerationJob] = useState(null);
    const [routesLoaded, setRoutesLoaded] = useState(false);
    const [proposals, setProposals] = useState([]);
    const visibleProposals = proposals.filter(proposal => !routeIds.length || routeIds.includes(Number(proposal.route_id)));
    const dispatchRouteIds = [...new Set(visibleProposals.map(proposal => Number(proposal.route_id)))];
    const [selected, setSelected] = useState(null);
    const [items, setItems] = useState([]);
    const [focused, setFocused] = useState(false);
    const [generationOpen, setGenerationOpen] = useState(false);
    const [machineSearch, setMachineSearch] = useState('');
    const [section, setSection] = useState('route-machines');
    useEffect(() => { const content = document.querySelector('[aria-label="Contenido de la ruta"]'); if (content) content.scrollTop = 0; }, [section]);
    const [machineFilter, setMachineFilter] = useState('all');
    const [orderReason, setOrderReason] = useState('');
    const [orderChanged, setOrderChanged] = useState(false);
    const [loading, setLoading] = useState(false);
    const [timesDirty, setTimesDirty] = useState(false);
    const [timeEditorRevision, setTimeEditorRevision] = useState(0);
    const [history, setHistory] = useState(null);
    const discardPendingTimes = () => {
        if (loading || !timesDirty || !window.confirm('¿Descartar los tiempos y horarios sin guardar? Se conservarán los valores guardados de esta propuesta.')) return;
        setTimesDirty(false);
        setTimeEditorRevision(current => current + 1);
    };
    const requestSequence = useRef(0);
    const savingMachines = useRef(false);
    useEffect(() => {
        const content = document.querySelector('.route-planning-workspace [aria-label="Contenido de la ruta"]');
        if (content) content.scrollTop = 0;
    }, [section, selected?._id]);
    const changes = machineChanges(items, selected?.items || []);
    const machineDirty = changes.length > 0 || orderChanged || Boolean(orderReason.trim());
    const dirty = machineDirty || timesDirty;
    const mayDiscard = () => !dirty || window.confirm('Hay cambios sin guardar. ¿Descartarlos y continuar?');

    useEffect(() => {
        const protect = (event) => {
            if (!dirty) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', protect);
        return () => window.removeEventListener('beforeunload', protect);
    }, [dirty]);

    const changeDate = (date) => {
        if (loading || dailySending || !date || !mayDiscard()) return;
        requestSequence.current += 1;
        setSelected(null);
        setItems([]);
        setProposals([]);
        setHistory(null);
        setRouteIds([]);
        setRouteOptions([]);
        setRoutesLoaded(false);
        setGenerationResults([]);
        setGenerationJob(null);
        setServiceDate(date);
    };
    const selectProposal = (proposal) => {
        if (loading || !mayDiscard()) return;
        setSelected(proposal);
        setFocused(true);
        setMachineSearch('');
        setSection('route-machines');
        setHistory(null);
    };

    const request = async (method, endpoint, data) =>
        axios({
            method,
            url: baseURL + endpoint,
            data,
            headers: { authorization: 'Bearer ' + context.token, 'Content-Type': 'application/json' }
        });

    const load = async (confirmDiscard = true) => {
        if (confirmDiscard && !mayDiscard()) return;
        const sequence = ++requestSequence.current;
        const date = apiDate(serviceDate);
        if (!date) return;
        setLoading(true);
        try {
            const [response, routesResponse] = await Promise.all([
                request('get', endPoints.parlevelRouteProposals('serviceDate=' + encodeURIComponent(date))),
                request('get', endPoints.parlevelPlanningRoutes(date))
            ]);
            if (sequence !== requestSequence.current) return;
            if (response.data?.capabilities) setCapabilities(response.data.capabilities);
            const available = unwrap(routesResponse);
            setRouteOptions(Array.isArray(available) ? available.filter((route) => route.available) : []);
            setRoutesLoaded(true);
            const data = unwrap(response);
            setProposals(Array.isArray(data) ? data : []);
            if (selected) {
                const refreshed = (data || []).find((proposal) => proposal._id === selected._id);
                setSelected(refreshed || null);
            }
        } catch (error) {
            if (sequence === requestSequence.current) toast.error(error?.response?.data?.message || 'No se pudieron cargar las propuestas.');
        } finally {
            if (sequence === requestSequence.current) setLoading(false);
        }
    };

    useEffect(() => {
        load(false);
        return () => { requestSequence.current += 1; };
        // Date changes are the only automatic refresh trigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceDate]);

    const machineFilterProposal = useRef(null);
    useEffect(() => {
        const nextItems = (selected?.items || []).map((item) => ({ ...item }));
        setItems(nextItems);
        if (machineFilterProposal.current !== selected?._id) {
            setMachineFilter(nextItems.some((item) => item.requires_manual_review && !item.review_resolved) ? 'review' : 'all');
            machineFilterProposal.current = selected?._id;
        }
        setOrderReason('');
        setOrderChanged(false);
    }, [selected]);

    const acceptGenerationResult = (result, confirmDiscard = true) => {
        if (!result || (confirmDiscard && !mayDiscard())) return;
            if (result?.preview_id) { applyPreview(result.preview_id); return; }
            const generated = result?.proposals || [];
            setGenerationResults([
                ...generated.map((proposal) => ({ name: proposal.route_name, message: statusLabel(proposal.status, proposal.items) })),
                ...(result?.errors || []).map((error) => ({ id: error.route_id, name: error.route_name, message: routeGenerationMessage(error), failed: true }))
            ]);
            const merged = [...proposals.filter((proposal) => !generated.some((next) => next._id === proposal._id)), ...generated];
            setProposals(merged);
            setHistory(null);
            const nextSelected = merged.find((proposal) => proposal._id === selected?._id) || generated[0] || null;
            setSelected(nextSelected ? { ...nextSelected } : null);
            toast.success('Propuestas generadas: ' + Number(result?.proposal_count || 0));
            if (result?.error_count) {
                const routeNames = (result?.errors || []).map((error) => error.route_name).filter(Boolean).join(', ');
                toast.warn('Rutas sin propuesta: ' + result.error_count + (routeNames ? ' (' + routeNames + ')' : ''));
            }
    };

    const generate = async (retryIds) => {
        if (!routesLoaded || dailySending || !mayDiscard()) return;
        const requestedIds = Array.isArray(retryIds) ? retryIds : routeIds;
        setLoading(true);
        try {
            const response = await request('post', capabilities?.background_generation ? 'parlevel/route-generation-jobs' : endPoints.parlevelRouteProposalGenerate, {
                serviceDate: apiDate(serviceDate), dailyPolicy,
                ...(requestedIds.length ? { routeIds: requestedIds } : {}),
            });
            const result = unwrap(response);
            if (capabilities?.background_generation) setGenerationJob(result);
            else acceptGenerationResult(result, false);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudieron generar las rutas.');
        } finally {
            setLoading(false);
        }
    };

    // Complete recoverable previews created by older server versions without another confirmation.
    const applyPreview = async (previewId) => {
        setLoading(true);
        try {
            const response = await request('post', 'parlevel/route-generation-previews/' + previewId + '/apply', { mode: 'preserve', reason: 'Regeneración automática: conservar ajustes compatibles con la fuente actual.' });
            const result = unwrap(response);
            const generated = result.proposals || [];
            const merged = [...proposals.filter(proposal => !generated.some(next => next._id === proposal._id)), ...generated];
            setProposals(merged); setHistory(null);
            setSelected(merged.find(proposal => proposal._id === selected?._id) || generated[0] || null);
            setGenerationResults([...generated.map(proposal => ({ name: proposal.route_name, message: statusLabel(proposal.status, proposal.items) })),
                ...(result.errors || []).map(error => ({ id: error.route_id, name: error.route_name, message: routeGenerationMessage(error), failed: true }))]);
            if (result.error_count) toast.warn('Algunas propuestas cambiaron durante la revisión. Actualice y reintente esas rutas.');
            else toast.success('Nueva versión guardada. La anterior permanece en el historial.');
        } catch (error) { toast.error(error?.response?.data?.message || 'No se pudo aplicar la vista previa. La versión anterior sigue guardada.'); }
        finally { setLoading(false); }
    };

    const stageMachineSelection = (machineIds, include) => {
        if (!editable || savingMachines.current || orderChanged) return;
        const targets = new Set(machineIds);
        const decision = include ? 'included' : 'excluded';
        setItems(current => current.map(item => targets.has(item.machine_id) && (!include || !item.source_context?.planning_excluded)
            ? { ...item, selected: include, review_decision: decision, review_resolved: true, review_resolution: decision }
            : item));
    };

    const mutateItem = (machineId, patch) => {
        if ('selected' in patch || ['included', 'excluded'].includes(patch.review_decision)) {
            stageMachineSelection([machineId], patch.review_decision ? patch.review_decision === 'included' : patch.selected);
            return;
        }
        if (orderChanged) {
            toast.warn('Guarde o cancele el nuevo orden antes de modificar máquinas.');
            return;
        }
        setItems((current) => current.map((item) => {
            if (item.machine_id !== machineId) return item;
            const next = { ...item, ...patch };
            if (item.requires_manual_review && ('selected' in patch || 'action_name' in patch) && !('review_decision' in patch)) {
                next.review_decision = 'pending';
                next.review_resolved = false;
            }
            if (patch.review_decision) {
                next.review_resolved = patch.review_decision !== 'pending';
                if (patch.review_decision !== 'pending') next.selected = patch.review_decision === 'included';
            }
            return next;
        }));
    };

    const applyLocationOrder = (locationIds) => {
        const orderByLocation = new Map(locationIds.map((id, index) => [String(id), index + 1]));
        setItems((current) => current.map((item) => ({
            ...item,
            route_order: item.selected
                ? orderByLocation.get(String(item.location_id)) || item.route_order
                : null
        })));
        setOrderChanged(true);
    };

    const cancelOrderChanges = () => {
        setItems((selected?.items || []).map((item) => ({ ...item })));
        setOrderReason('');
        setOrderChanged(false);
    };

    const saveChanges = async () => {
        if (savingMachines.current) return;
        if (!changes.length) return toast.info('No hay cambios pendientes.');
        savingMachines.current = true;
        setLoading(true);
        try {
            const response = await request('patch', endPoints.parlevelRouteProposal(selected._id), {
                version: selected.version,
                changes
            });
            const updated = unwrap(response);
            setSelected(updated);
            setProposals((current) => current.map((proposal) => proposal._id === updated._id ? updated : proposal));
            toast.success('Selección guardada en Syncro.');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudieron guardar los cambios.');
        } finally {
            savingMachines.current = false;
            setLoading(false);
        }
    };

    const saveOrder = async () => {
        if (!orderChanged) return toast.info('El orden de paradas no cambió.');
        if (!orderReason.trim()) return toast.warn('Indique la razón del cambio de orden.');
        const locationIds = buildRouteStops(items).map((stop) => stop.key);
        setLoading(true);
        try {
            const response = await request('patch', endPoints.parlevelRouteProposalOrder(selected._id), {
                version: selected.version,
                location_ids: locationIds,
                reason: orderReason.trim()
            });
            const updated = unwrap(response);
            setSelected(updated);
            setProposals((current) => current.map((proposal) => proposal._id === updated._id ? updated : proposal));
            setOrderChanged(false);
            setOrderReason('');
            toast.success('Nuevo orden guardado y recorrido recalculado.');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudo guardar el orden de paradas.');
        } finally {
            setLoading(false);
        }
    };

    const reject = async () => {
        if (!mayDiscard()) return;
        const rejectionReason = window.prompt('Razón para rechazar esta propuesta:');
        if (!rejectionReason?.trim()) return;
        setLoading(true);
        try {
            const response = await request('post', endPoints.parlevelRouteProposalReject(selected._id), {
                version: selected.version,
                reason: rejectionReason
            });
            const updated = unwrap(response);
            setSelected(updated);
            setProposals((current) => current.map((proposal) => proposal._id === updated._id ? updated : proposal));
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudo rechazar la propuesta.');
        } finally {
            setLoading(false);
        }
    };

    const optimizeSchedule = async () => {
        if (dirty) return;
        const reason = window.prompt('Motivo para ajustar el orden usando los horarios de acceso. Se evaluarán hasta cuatro alternativas y se guardará la viable de menor jornada estimada.');
        if (!reason?.trim()) return;
        setLoading(true);
        try {
            const response = await request('post', endPoints.parlevelRouteProposal(selected._id) + '/optimize-schedule', { version: selected.version, reason });
            const updated = unwrap(response);
            setSelected(updated); setProposals(current => current.map(proposal => proposal._id === updated._id ? updated : proposal));
            toast.success('Orden recalculado con los horarios conocidos. Revise las llegadas y los avisos.');
        } catch (error) { toast.error(error?.response?.data?.message || 'No se pudo calcular un orden por horarios.'); }
        finally { setLoading(false); }
    };

    const saveTimes = async (settings, reason, useAsTemplate = false) => {
        if (machineDirty) return;
        setLoading(true);
        try {
            const response = await request('patch', endPoints.parlevelRouteProposal(selected._id) + '/operational-settings', { version: selected.version, settings, reason });
            const updated = unwrap(response);
            setSelected(updated);
            setProposals(current => current.map(proposal => proposal._id === updated._id ? updated : proposal));
            if (useAsTemplate) {
                try {
                    const template = unwrap(await request('get', endPoints.parlevelRouteProposal(updated._id) + '/template'));
                    await request('put', endPoints.parlevelRouteProposal(updated._id) + '/template', { version: updated.version, templateVersion: template?.version || 0, operation: 'save', reason });
                    toast.success('Valores habituales guardados para próximas propuestas.');
                } catch (error) { toast.error('Los tiempos se guardaron, pero no se pudo actualizar la plantilla. Revísela en Plantilla de esta ruta.'); }
            }
            toast.success('Tiempos guardados y jornada recalculada.');
            return true;
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudieron guardar los tiempos.');
            return false;
        } finally { setLoading(false); }
    };

    const finalize = async () => {
        if (dirty || !items.some((item) => item.selected)) return;
        const summary = selected.route_name + ' · ' + selected.service_date + '\n' +
            items.filter((item) => item.selected).length + ' máquinas seleccionadas\n' +
            'Conductor: ' + (selected.driver_id || 'Sin datos') + ' · Vehículo: ' + (selected.truck_id || 'Sin datos') +
            ' · Bodega: ' + (selected.warehouse_id || 'Sin datos') + '\n' +
            ACTIONS.map((action) => action + ': ' + items.filter((item) => item.selected && item.action_name === action).length).join(' · ') +
            '\nJornada estimada: ' + formatDurationMinutes(routeEstimate.operationalMinutes) +
            '\nHorarios: ' + (selected.route_optimization?.schedule?.warnings || ['Sin configurar']).join(' · ') +
            '\nCapacidad de carga no evaluada. Disponibilidad de conductor y vehículo sujeta a los horarios acreditados; pueden existir asignaciones externas sin horario verificable.\nSe guardará el plan aprobado y se creará el prekit. ¿Aprobar y enviar?';
        if (!window.confirm(summary)) return;
        setLoading(true);
        try {
            const response = await request('post', endPoints.parlevelRouteProposalFinalize(selected._id), {
                version: selected.version
            });
            const updated = unwrap(response);
            setSelected(updated);
            setProposals((current) => current.map((proposal) => proposal._id === updated._id ? updated : proposal));
            if (updated.status === 'submitted') toast.success('Servicio confirmado. Consulte el alcance de verificación en Historial.');
            else toast.warn('Envío pendiente de verificación. No se repetirá la creación.');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Parlevel no confirmó la ruta.');
            await load(false);
        } finally {
            setLoading(false);
        }
    };

    const verify = async () => {
        setLoading(true);
        try {
            const response = await request('post', endPoints.parlevelRouteProposalReconcile(selected._id), { version: selected.version });
            const updated = unwrap(response);
            setSelected(updated);
            setProposals((current) => current.map((proposal) => proposal._id === updated._id ? updated : proposal));
            if (updated.status === 'submitted') toast.success('Servicio confirmado. Consulte el alcance de verificación en Historial.');
            else toast.info(updated.failure?.message || 'La verificación sigue pendiente.');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudo consultar Parlevel; la creación no se repitió.');
        } finally { setLoading(false); }
    };
    const loadHistory = async () => {
        setLoading(true);
        try {
            const response = await request('get', endPoints.parlevelRouteProposalHistory(selected._id));
            setHistory(unwrap(response));
        } catch (error) { toast.error('No se pudieron consultar las versiones anteriores.'); }
        finally { setLoading(false); }
    };

    const selectedCount = items.filter((item) => item.selected).length;
    const unresolvedCount = items.filter((item) => item.requires_manual_review && !item.review_resolved).length;
    const optimization = selected?.route_optimization || {};
    const routeEstimate = routeEstimateDisplay(selected || {}, items);
    const canEditProposal = canWrite && !loading && ['draft', 'ready_for_review'].includes(selected?.status);
    const editable = canEditProposal && !timesDirty;
    const selectionChanged = items.some((item) => {
        const original = (selected?.items || []).find((candidate) => candidate.machine_id === item.machine_id);
        return original && (original.selected !== item.selected || original.action_name !== item.action_name);
    });
    const filteredItems = items.filter((item) => {
        if (![item.machine_name, item.location_name].join(' ').toLocaleLowerCase('es').includes(machineSearch.toLocaleLowerCase('es'))) return false;
        if (machineFilter === 'selected') return item.selected;
        if (machineFilter === 'review') return item.requires_manual_review && !item.review_resolved;
        return true;
    });

    const missingResources = selected ? [['driver', 'Conductor'], ['truck', 'Vehículo'], ['warehouse', 'Bodega']].filter(([key]) => !selected[key + '_id'] || !selected.resources?.[key]?.name).map(([, label]) => label) : [];
    const availabilityProblems = availabilityCheck && selected && availabilityCheck.id === selected._id && availabilityCheck?.version === selected?.version ? availabilityCheck.problems : [];
    const blockers = [];
    if (selected) {
        if (missingResources.length) blockers.push({ text: missingResources.join(', ') + ': identidad sin confirmar; actualice la fuente y regenere', target: 'route-logistics' });
        if (!selectedCount) blockers.push({ text: 'No hay máquinas seleccionadas', target: 'route-machines' });
        if (unresolvedCount) blockers.push({ text: unresolvedCount + ' máquina(s) requieren decisión.', target: 'route-machines' });
        if (dirty) blockers.push({ text: timesDirty ? 'Tiempos y horarios sin guardar' : 'Cambios de máquinas u orden sin guardar', target: timesDirty ? 'route-times' : orderChanged ? 'route-map' : 'route-machines' });
        availabilityProblems.forEach(text => blockers.push({ text, target: 'route-logistics' }));
        (selected.load_assessment?.problems || []).forEach(text => blockers.push({ text, target: 'route-logistics' }));
        if (selected.daily_policy?.mode !== 'daily' && selected.load_assessment && (!selected.availability_review || Date.now() - Date.parse(selected.availability_review.recorded_at) >= 86400000)) blockers.push({ text: 'Disponibilidad de recursos pendiente de confirmar', target: 'route-logistics' });
        (selected.priority_problems || []).forEach(text => blockers.push({ text, target: 'route-machines' }));
        if (selected.status !== 'ready_for_review' && !blockers.length) blockers.push({ text: 'La propuesta no está lista para aprobar', target: 'route-history' });
    }
    const warnings = [...(optimization.schedule?.problems || []), ...(optimization.schedule?.warnings || ['Horarios de acceso y límite de jornada sin configurar'])].map(text => ({ text, target: 'route-times' }));
    const approveDisabled = availabilityProblems.length > 0 || Boolean(selected?.load_assessment?.problems?.length) || Boolean(selected?.daily_policy?.mode !== 'daily' && selected?.load_assessment && (!selected.availability_review || Date.now() - Date.parse(selected.availability_review.recorded_at) >= 86400000)) || selected?.status !== 'ready_for_review' || unresolvedCount > 0 || dirty || !selectedCount || Boolean(selected?.priority_problems?.length) || missingResources.length > 0;
    const partialEstimate = ['preparation_minutes', 'closing_minutes', 'break_minutes'].some(field => selected?.operational_settings?.[field] == null);

    return (
        <PlanningWorkspace
            hideNavigation={selected?.daily_policy?.mode === 'daily'}
            focused={focused && Boolean(selected)}
            overview={<><RepairServices proposals={visibleProposals} date={apiDate(serviceDate)} request={request} canApprove={canApprove} disabled={loading || generationJob?.status === 'running'} /><DailyDispatch date={apiDate(serviceDate)} routeIds={dispatchRouteIds} request={request} canApprove={canApprove} disabled={loading || generationJob?.status === 'running' || !visibleProposals.length} onComplete={() => load(false)} onBusy={setDailySending} /><RouteOverview proposals={visibleProposals} onSelect={selectProposal} statusLabel={statusLabel} busy={loading} generating={generationJob?.status === 'running'} /></>}
            context={selected && <Box sx={{ mt: 8, pt: 3, borderTop: '1px solid #e0e6ef' }}><Typography variant="overline" fontWeight={700} color="text.secondary">Indicadores del plan</Typography>{[['Máquinas', selectedCount], ['Ubicaciones', new Set(items.filter(item => item.selected).map(item => item.location_id)).size], ['Jornada', formatDurationMinutes(routeEstimate.operationalMinutes)]].map(([label, value]) => <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 0.75 }}><Typography color="text.secondary">{label}</Typography><Typography fontWeight={700}>{value}</Typography></Stack>)}{partialEstimate && <Typography color="warning.main" variant="body2">Estimación parcial</Typography>}<Box component="details" sx={{ mt: 2 }}><Typography component="summary" variant="caption" sx={{ cursor: 'pointer' }}>Confianza y datos del plan</Typography><Typography variant="body2">{routeEstimate.confidence.score ?? '—'}/100 · {routeEstimate.confidence.label}</Typography><Typography variant="caption">Candidatas: {items.length} · Distancia: {optimization.total_distance_meters == null ? 'Sin datos' : metricDisplay(optimization.total_distance_meters / 1000, ' km')} · Conducción: {formatDurationMinutes(routeEstimate.drivingMinutes)}</Typography><Typography variant="caption" display="block">Calidad de datos: {routeEstimate.confidence.components.data_quality}/40 · Recorrido: {routeEstimate.confidence.components.route_coverage}/25 · Coordenadas: {routeEstimate.confidence.components.location_data}/15 · Cantidad: {routeEstimate.confidence.components.capacity_fit}/20</Typography></Box></Box>}
            tabs={selected && <>
                <RouteTabs simplified={selected.daily_policy?.mode === 'daily'} value={section} onChange={setSection} blockers={blockers} warnings={warnings} />
                {timesDirty && <Alert severity="warning" sx={{ mx: 2, my: 1 }}>
                    <Typography variant="body2">Hay tiempos u horarios sin guardar. Guarda o descarta ese ajuste para volver a editar máquinas y el orden. Los filtros siguen disponibles.</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                        {section !== 'route-times' && <MuiButton onClick={() => setSection('route-times')}>Revisar tiempos pendientes</MuiButton>}
                        <MuiButton onClick={discardPendingTimes} disabled={loading}>Descartar tiempos pendientes</MuiButton>
                    </Stack>
                </Alert>}
            </>}
            toolbar={<>
                <Box hidden={focused && Boolean(selected)} sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">Plataformas de máquinas / Planificación de rutas</Typography>
                    <Stack direction="row" flexWrap="wrap" justifyContent="space-between" gap={2} sx={{ mt: 3, mb: 2 }}><Box><Typography component="h1" variant="h5" fontWeight={700}>Planificación de rutas</Typography><Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}><Chip variant="outlined" label={'Servicio ' + sourceDateDisplay(apiDate(serviceDate))} onClick={() => setGenerationOpen(true)} /><Chip variant="outlined" label={routeIds.length ? routeIds.length + ' rutas elegidas' : 'Todas las rutas disponibles · ' + routeOptions.length} /></Stack></Box><Stack direction="row" alignItems="center" gap={1}><MuiButton variant="outlined" onClick={() => load()} disabled={loading}>Actualizar</MuiButton>{canWrite && <MuiButton variant="contained" onClick={() => setGenerationOpen(true)}>+ Nueva generación</MuiButton>}</Stack></Stack>
                </Box>
                <Dialog open={generationOpen} onClose={() => !loading && setGenerationOpen(false)} fullWidth maxWidth="sm" aria-labelledby="generation-title">
                    <DialogTitle id="generation-title" sx={{ pt: 3, px: 3 }}>Generar propuestas<MuiButton aria-label="Cerrar generación" onClick={() => setGenerationOpen(false)} disabled={loading} sx={{ float: 'right', minWidth: 32 }}>×</MuiButton></DialogTitle>
                    <DialogContent sx={{ px: 3, '& .generation-fields': { display: 'grid', gap: 3 }, '& .MuiOutlinedInput-root': { minHeight: 48 } }}><Typography color="text.secondary" sx={{ mb: 3 }}>Elija la fecha de servicio y las rutas. Generar no crea servicios en Parlevel.</Typography><div className="generation-fields">
                        <div>
                            <label htmlFor="route-service-date" className="block mb-2 font-semibold">Fecha de servicio</label>
                            <TextField id="route-service-date" fullWidth type="date" value={serviceDate ? `${serviceDate.getFullYear()}-${String(serviceDate.getMonth() + 1).padStart(2, '0')}-${String(serviceDate.getDate()).padStart(2, '0')}` : ''} onChange={event => { const parts = event.target.value.split('-').map(Number); if (parts.length === 3 && parts.every(Boolean)) changeDate(new Date(parts[0], parts[1] - 1, parts[2], 12)); }} disabled={loading} inputProps={{ 'aria-label': 'Fecha de servicio' }} />
                        </div>
                        <div>
                            <label htmlFor="planning-routes" className="block mb-2 font-semibold">Rutas a generar</label>
                            <Autocomplete multiple id="planning-routes" options={routeOptions} value={routeOptions.filter(route => routeIds.includes(route.id))} getOptionLabel={route => route.name || String(route.id)} isOptionEqualToValue={(option, value) => option.id === value.id} onChange={(_, routes) => setRouteIds(routes.map(route => route.id))} disabled={loading || !routesLoaded} disableCloseOnSelect clearText="Limpiar" closeText="Cerrar" openText="Elegir rutas" noOptionsText="Sin rutas disponibles" renderInput={params => <TextField {...params} placeholder={routeIds.length ? 'Agregar o quitar rutas…' : 'Todas las rutas disponibles · ' + routeOptions.length} />} />
                        </div>

                    </div>
                    <Typography variant="body2" sx={{ mt: 2 }}>Reposición: inventario verificado de 80% o menos. Por encima de 80% se evalúa solo cobro; se respetan averías, compromisos y decisiones manuales. Café sin inventario verificable: según frecuencia.</Typography>
                    <Box component="details" sx={{ mt: 2 }}><summary>Reglas automáticas · configuración habitual en este navegador</summary><Stack direction={{ xs: 'column', sm: 'row' }} gap={2} sx={{ mt: 2 }}>
                        <TextField label="Puntaje mínimo (0–100)" type="number" value={dailyPolicy.minimum_score ?? ''} onChange={e => setDailyRule('minimum_score', e.target.value)} inputProps={{ min: 0, max: 100 }} helperText="Vacío: no excluir por puntaje. Solo datos completos; respeta prioridades." />
                        <TextField label="Café: servicio cada cuántos días" type="number" value={dailyPolicy.coffee_every_days ?? ''} onChange={e => setDailyRule('coffee_every_days', e.target.value)} inputProps={{ min: 1, max: 30, step: 1 }} helperText="Vacío: sin regla de frecuencia. Usa el contador de planificación; respeta reparaciones pendientes." />
                        <TextField label="Reparación importante desde (1–10)" type="number" value={dailyPolicy.repair_important_priority ?? 7} onChange={e => setDailyRule('repair_important_priority', e.target.value)} inputProps={{ min: 1, max: 10, step: 1 }} />
                        <TextField label="Reparación crítica desde (1–10)" type="number" value={dailyPolicy.repair_critical_priority ?? 9} onChange={e => setDailyRule('repair_critical_priority', e.target.value)} inputProps={{ min: 1, max: 10, step: 1 }} helperText="Las críticas pueden superar las 25 máquinas iniciales." />
                        <Typography variant="body2">Hasta 25 máquinas elegibles, priorizando reparaciones pendientes. Los avisos de datos no requieren decisiones individuales. Puede excluir máquinas manualmente; las restricciones de Parlevel se mantienen.</Typography>
                    </Stack><Typography variant="body2" sx={{ mt: 1 }}>Se conservan decisiones manuales. Tiempos y recorrido se calculan con los valores habituales disponibles; carga opcional. Los datos faltantes permanecen como avisos.</Typography></Box>
                    <small className="block mt-2">Generar propuestas no crea servicios en Parlevel. Sin selección se incluyen todas las rutas disponibles.</small>
                    {proposals.length > 0 && <Alert severity="warning" sx={{ mt: 3 }}>Se guardará una nueva versión. Se conservarán los ajustes compatibles y la versión anterior quedará en el historial.</Alert>}
                    </DialogContent><DialogActions sx={{ p: 3, borderTop: '1px solid #e0e6ef' }}><MuiButton variant="outlined" disabled={loading} onClick={() => setGenerationOpen(false)}>Cancelar</MuiButton><MuiButton variant="contained" onClick={() => { setGenerationOpen(false); generate(); }} disabled={!canWrite || loading || dailySending || !routesLoaded || generationJob?.status === 'running'}>Generar propuestas</MuiButton></DialogActions>
                </Dialog>
                <Box hidden={focused && Boolean(selected)}>
                    {generationResults.length > 0 && <details>
                        <summary>{generationResults.filter(result => !result.failed && result.message !== 'Sin máquinas seleccionadas').length} propuestas disponibles · {generationResults.filter(result => result.message === 'Sin máquinas seleccionadas').length} rutas sin máquinas · {generationResults.filter(result => result.failed).length} sin propuesta — Ver detalle</summary>
                        <ul aria-label="Resultado por ruta">{generationResults.filter(result => result.failed || result.message === 'Sin máquinas seleccionadas').map((result, index) =>
                            <li key={index}>{result.name}: {result.message}</li>)}</ul>
                    </details>}
                    {generationResults.some(result => result.failed && result.id) && <MuiButton disabled={loading} onClick={() => generate(generationResults.filter(result => result.failed && result.id).map(result => result.id))}>Reintentar solo rutas fallidas</MuiButton>}
                    {capabilities?.background_generation && <GenerationProgress key={apiDate(serviceDate)} blocked={dirty || loading} date={apiDate(serviceDate)} token={context.token} job={generationJob} onJob={setGenerationJob} onResult={acceptGenerationResult} />}
                </Box>
</>}
            navigation={null}
            header={selected && <RouteFocusHeader section={section} selected={selected} proposals={visibleProposals} onSelect={selectProposal} onBack={() => { if (!loading && mayDiscard()) { setFocused(false); setSelected(null); } }} onNavigate={setSection} status={statusLabel(selected.status, items)} date={sourceDateDisplay(selected.service_date)} blockers={blockers} warnings={warnings} busy={loading} />}
            actions={selected?.status === 'submitted' ? <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center"><Typography variant="body2">Servicio enviado · plan aprobado conservado</Typography><MuiButton onClick={() => setSection('route-follow-up')}>Ver seguimiento</MuiButton><MuiButton onClick={() => setSection('route-history')}>Ver verificación</MuiButton></Stack> : selected && (canWrite || canApprove) ? <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2}><MuiButton color="error" variant="outlined" onClick={reject} disabled={!editable}>Rechazar ruta</MuiButton><Typography variant="body2" color="text.secondary">{section === 'route-review' ? 'Aprobar y enviar crea un servicio en Parlevel.' : dirty ? 'Cambios sin guardar. Continuar no guarda los ajustes.' : 'Cambios sin guardar: 0'}</Typography></Stack>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                        {canApprove && ['submitting', 'failed'].includes(selected.status) && <MuiButton onClick={verify} disabled={loading}>Verificar envío</MuiButton>}
                        {section === 'route-machines' && <MuiButton variant="outlined" onClick={saveChanges} disabled={!editable || !machineDirty}>Guardar cambios</MuiButton>}
                        {section === 'route-times' && <MuiButton variant="outlined" disabled={!canManageRules || !canEditProposal || machineDirty || !timesDirty} onClick={() => document.getElementById('route-times-save')?.click()}>Guardar y recalcular</MuiButton>}
                        {section === 'route-map' && orderChanged && <MuiButton variant="outlined" disabled={!editable} onClick={saveOrder}>Guardar orden y recalcular</MuiButton>}
                        {selected.daily_policy?.mode !== 'daily' && stepIndex(section) > 0 && <MuiButton onClick={() => setSection(preparationSteps[stepIndex(section) - 1].section)}>Anterior</MuiButton>}
                        {selected.daily_policy?.mode === 'daily' ? <MuiButton variant="contained" disabled={loading || dirty} onClick={() => { setFocused(false); setSelected(null); }}>Volver al plan del día</MuiButton> : section === 'route-review' ? <MuiButton variant="contained" onClick={finalize} disabled={!canApprove || loading || approveDisabled}>Aprobar y enviar</MuiButton> : <MuiButton variant="contained" onClick={() => setSection(preparationSteps[Math.min(Math.max(stepIndex(section), 0) + 1, 3)].section)}>Continuar</MuiButton>}
                    </Stack>
                </Stack> : <Typography variant="caption">{selected ? 'Acceso de consulta' : 'Elige una ruta para comenzar.'}</Typography>}
        >
            {selected && (
                <>
                    {stepIndex(section) >= 0 && <Stack direction={{ xs: 'column', xl: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}><Box><Typography component="h2" variant="h4" fontWeight={700}>{selected.daily_policy?.mode === 'daily' ? ({ 'route-machines': 'Máquinas y excepciones', 'route-times': 'Tiempos y horarios', 'route-map': 'Mapa y orden', 'route-logistics': 'Recursos y carga opcional', 'route-review': 'Resumen de la ruta' }[section]) : preparationSteps[stepIndex(section)].label}</Typography><Typography color="text.secondary" sx={{ mt: 1 }}>{preparationSteps[stepIndex(section)].hint}</Typography></Box>{selected.daily_policy?.mode !== 'daily' && stepIndex(section) === 1 && <Tabs value={section} onChange={(_, value) => setSection(value)} aria-label="Preparar recorrido" sx={{ bgcolor: '#e6ebf2', borderRadius: 2, alignSelf: 'start', '& .Mui-selected': { bgcolor: 'white', borderRadius: 2 } }}><Tab value="route-times" label="Tiempos y horarios" /><Tab value="route-map" label="Mapa y orden" /></Tabs>}</Stack>}
                    {section !== 'route-review' && <StepNotices blockers={blockers.filter(blocker => stepIndex(blocker.target) === stepIndex(section))} onNavigate={(target, notice) => { setSection(target); if (target === 'route-machines') setMachineFilter('review'); document.getElementById(target)?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }); if (/capacidad|carga/i.test(notice?.text || '')) document.getElementById('route-load-edit')?.click(); else if (/disponibilidad/i.test(notice?.text || '')) document.getElementById('route-availability-confirm')?.click(); }} />}
                    {section === 'route-review' && <div id="route-review" role="region" aria-label="Revisión final"><RouteReview machines={items.filter(item => item.selected).slice().sort((a, b) => (a.route_order || 0) - (b.route_order || 0))} name={selected.route_name} date={selected.service_date?.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$2/$1/$3')} count={selectedCount} locations={new Set(items.filter(item => item.selected).map(item => item.location_id)).size} duration={formatDurationMinutes(routeEstimate.operationalMinutes)} partial={partialEstimate} dirty={dirty} ready={!approveDisabled} blockers={blockers} warnings={warnings} resources={['driver', 'truck', 'warehouse'].map((key, index) => ({ label: ['Conductor', 'Vehículo', 'Bodega'][index], name: selected.resources?.[key]?.name }))} onNavigate={setSection} /></div>}
                    <div id="route-machines" hidden={section !== 'route-machines'} role="region" aria-label="Revisar máquinas" className="col-12 p-2">
                        <Card title="Máquinas y puntuación">
                            {selected.daily_policy?.mode !== 'daily' && <PriorityRules key={selected._id + ":" + selected.version} proposal={selected} token={context.token} editable={canManageRules && canEditProposal && !dirty} onChange={updated => {
                                setSelected(updated); setProposals(current => current.map(proposal => proposal._id === updated._id ? updated : proposal));
                            }} />}
                            {selectedCount === 0 && <p>Esta propuesta no tiene máquinas seleccionadas y no se puede enviar.</p>}
                            <div className="flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                                <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar máquinas">
                                    <Chip label={'Todas (' + items.length + ')'} aria-pressed={machineFilter === 'all'} variant={machineFilter === 'all' ? 'filled' : 'outlined'} color={machineFilter === 'all' ? 'primary' : 'default'} onClick={() => setMachineFilter('all')} />
                                    <Chip label={'Seleccionadas (' + selectedCount + ')'} aria-pressed={machineFilter === 'selected'} variant={machineFilter === 'selected' ? 'filled' : 'outlined'} color={machineFilter === 'selected' ? 'primary' : 'default'} onClick={() => setMachineFilter('selected')} />
                                    <Chip label={'Requieren decisión (' + unresolvedCount + ')'} aria-pressed={machineFilter === 'review'} variant={machineFilter === 'review' ? 'filled' : 'outlined'} color={machineFilter === 'review' ? 'primary' : 'default'} onClick={() => setMachineFilter('review')} />
                                </div>
                                <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2}><Typography variant="body2" color="text.secondary">{filteredItems.length} máquinas visibles</Typography><TextField label="Buscar máquina o ubicación" value={machineSearch} onChange={event => setMachineSearch(event.target.value)} /></Stack>
                            </div>

                            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mb: 2 }}>
                                <MuiButton variant="outlined" disabled={!editable || orderChanged || !filteredItems.some(item => !item.source_context?.planning_excluded)} onClick={() => stageMachineSelection(filteredItems.map(item => item.machine_id), true)}>Incluir todas</MuiButton>
                                <MuiButton variant="outlined" disabled={!editable || orderChanged || !filteredItems.length} onClick={() => stageMachineSelection(filteredItems.map(item => item.machine_id), false)}>Excluir todas</MuiButton>
                                <Typography variant="caption" color="text.secondary">Aplica solo a las {filteredItems.length} máquinas visibles en «{machineFilter === 'review' ? 'Requieren decisión' : machineFilter === 'selected' ? 'Seleccionadas' : 'Todas'}», respetando la búsqueda. Se guarda en Syncro; no envía a Parlevel. Incluir respeta las exclusiones de Parlevel.</Typography>
                            </Stack>
                            <MachineReviewTable key={selected._id} rows={filteredItems} allRows={items} routeName={selected.route_name} editable={editable} changedIds={changes.map(change => change.machine_id)} onChange={mutateItem} details={(row) => {
                                const communication = communicationDisplay(row);
                                const evidence = row.source_context?.recent_service_evidence;
                                const serviceAction = { 'Service Only': 'Solo servicio', 'Serv & Col': 'Servicio y cobro', 'Service & Collect': 'Servicio y cobro', 'Collect Only': 'Solo cobro', Repair: 'Reparación' };
                                const machineType = { Coffee: 'Café', Drink: 'Bebidas', Snack: 'Snacks', Combo: 'Combinada' }[machineTypeDisplay(row).type] || machineTypeDisplay(row).type;
                                const metrics = [
                                    ['Inventario', metricDisplay(row.metrics?.parlevel_pct, '%')],
                                    ['Días informados sin servicio', metricDisplay(row.metrics?.days_since_service)],
                                    ['Efectivo', metricDisplay(row.metrics?.expected_cash)],
                                    ['Productos agotados', metricDisplay(row.metrics?.sold_outs)],
                                    ['Avería informada', row.metrics?.has_breakdown == null ? 'Sin datos' : row.metrics.has_breakdown ? 'Sí' : 'No']
                                ];
                                return <Stack gap={2.5}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                                        <Typography variant="body2" color="text.secondary">{machineType}{row.make_model ? ' · ' + row.make_model : ''}</Typography>
                                        <Box sx={{ textAlign: 'right' }}><Typography variant="caption" color="text.secondary">Puntaje de prioridad</Typography><Typography variant="h6" fontWeight={600}>{metricDisplay(row.score)}</Typography>
                                            {Boolean(row.data_quality?.missing_metrics?.length) && <Typography variant="caption">Parcial · {row.data_quality.known_metrics}/5 métricas</Typography>}
                                            {!row.data_quality && <Typography variant="caption">Faltantes no identificados</Typography>}
                                        </Box>
                                    </Stack>
                                    <Box component="section" aria-label="Datos de selección">
                                        <Typography component="h3" variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Datos de selección</Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                                            {metrics.map(([label, value]) => <Box key={label} sx={{ p: 1.5, bgcolor: '#f4f6f8', borderRadius: 1.5 }}><Typography variant="caption" color="text.secondary" display="block">{label}</Typography><Typography variant="body1" fontWeight={500}>{value}</Typography></Box>)}
                                        </Box>
                                        {row.source_context?.inventory_date && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Inventario consultado para el {sourceDateDisplay(row.source_context.inventory_date)} · selección automática menor al 80%.</Typography>}
                                        {row.source_context?.inventory_assessment === 'coffee_zero_unverified' && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Parlevel informa 0% para café, sin una base de existencias verificable. No se puntúa como vacío.</Typography>}
                                        {row.source_context?.planning_excluded && <Alert severity="warning" sx={{ mt: 1 }}>Excluida de la selección de Parlevel. {row.source_context.planning_exclusion_reason || 'Motivo no informado'}</Alert>}
                                        <Box component="details" sx={{ mt: 1.5 }}><Typography component="summary" variant="body2" sx={{ cursor: 'pointer', color: 'primary.main' }}>Ver desglose y origen de los datos</Typography>
                                            <Stack gap={0.5} sx={{ mt: 1 }}>{scoreBreakdownDisplay(row).map(detail => <Stack key={detail.label} direction="row" justifyContent="space-between"><Typography variant="body2">{detail.label}</Typography><Typography variant="body2">{metricDisplay(detail.value)}</Typography></Stack>)}</Stack>
                                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>{daysSourceDisplay(row)}</Typography>
                                            <Typography variant="caption" display="block">Fecha de planificación: {sourceDateDisplay(selected.service_date)}</Typography>
                                            <Typography variant="caption" display="block">Catálogo consultado: {sourceDateDisplay(row.source_context?.inventory_fetched_at)}</Typography>
                                        </Box>
                                    </Box>
                                    <Box component="section" aria-label="Servicio y comunicación">
                                        <Typography component="h3" variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Servicio y comunicación</Typography>
                                        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                                            <Typography variant="body2" fontWeight={600}>Servicio registrado</Typography>
                                            {evidence ? <><Typography variant="body2" sx={{ mt: 0.5 }}>Ruta del {sourceDateDisplay(evidence.route_date)} · #{evidence.route_id}</Typography>
                                                <Typography variant="body2">Hora informada: {evidence.reported_time || 'Sin datos'} · {serviceAction[evidence.action] || 'Acción sin identificar'}</Typography>
                                                <Typography variant="caption" color="text.secondary">La fecha corresponde a la ruta; no confirma el momento exacto del último servicio.</Typography></>
                                                : <Typography variant="body2" color="text.secondary">Sin evidencia de servicio en las rutas consultadas.</Typography>}
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Último servicio informado en catálogo: {sourceDateDisplay(row.source_context?.last_service)}</Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1.5 }}><b>Última comunicación:</b> {communication.exact}</Typography>
                                        {communication.age && <Typography variant="caption" color="text.secondary" display="block">{communication.age}</Typography>}
                                        {communication.reasons && <Typography variant="body2" color="text.secondary">{communication.reasons}</Typography>}
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>La falta de comunicación no confirma que esté apagada o averiada, ni que no haya recibido servicio.</Typography>
                                        {!row.source_context && <Typography variant="caption">Genere una nueva versión para conservar las fuentes de servicio y sincronización.</Typography>}
                                    </Box>
                                </Stack>;
                            }} />

                        </Card>
                    </div>

            {selected && <div id="route-logistics" hidden={section !== 'route-logistics'} role="region" aria-label="Carga y recursos">
                <RouteLogistics key={selected._id + ':' + selected.version} proposal={selected} token={context.token} active={section === 'route-logistics'} canChangeDriver={canApprove && selected.status === 'submitted' && !dirty} onAvailability={problems => setAvailabilityCheck({ id: selected._id, version: selected.version, problems })} editable={canManageRules && canEditProposal && !dirty} onChange={updated => {
                    setSelected(updated); setProposals(current => current.map(row => row._id === updated._id ? updated : row));
                }} />
            </div>}
            {selected && <div id="route-times" hidden={section !== 'route-times'} role="region" aria-label="Tiempos y horarios"><Box>
                <RouteTemplate key={'template:' + selected._id + ':' + selected.version} proposal={selected} token={context.token} editable={canManageRules && canEditProposal && !dirty} />
                <RouteOperationalSettings key={selected._id + ':' + selected.version + ':' + timeEditorRevision} proposal={selected} editable={canManageRules && canEditProposal && !machineDirty} blocked={machineDirty} onDirty={setTimesDirty} onSave={saveTimes} estimate={routeEstimate} />
                <RouteScheduleSummary schedule={optimization.schedule} stale={dirty} />
                {(selectionChanged || orderChanged) && <p>Recorrido pendiente de recalcular. Guarde los cambios antes de aprobar.</p>}
            </Box></div>}

                    <div id="route-map" hidden={section !== 'route-map'} role="region" aria-label="Mapa y orden" className="col-12 p-2">
                        <Card title="Mapa y orden de paradas">
                            <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                <div>
                                    <Tag
                                        value={selected.route_optimization?.ordering_mode === 'manual' ? 'Orden manual' : 'Orden automático'}
                                        severity={selected.route_optimization?.ordering_mode === 'manual' ? 'warning' : 'info'}
                                    />
                                    <span className="ml-2 text-600">
                                        Arrastre las paradas o use las flechas para reorganizar el recorrido.
                                    </span>
                                </div>
                                {selectionChanged && (
                                    <span className="text-orange-700">
                                        Guarde primero los cambios de máquinas para habilitar el orden.
                                    </span>
                                )}
                            </div>
                            <MuiButton onClick={optimizeSchedule} disabled={!editable || dirty}>Optimizar con horarios</MuiButton>
                            <Typography variant="caption" display="block">Evalúa hasta cuatro órdenes con tiempos de conducción. Los horarios desconocidos continúan como avisos; no garantiza encontrar el mejor recorrido posible.</Typography>
                            <RouteCityMap
                                items={items}
                                schedule={optimization.schedule}
                                stale={dirty}
                                editable={editable && !changes.length}
                                onOrderChange={applyLocationOrder}
                            />
                            {editable && orderChanged && (
                                <div className="mt-4 p-3 border-1 surface-border border-round surface-50">
                                    <label className="block mb-2 font-semibold">Razón del cambio de orden</label>
                                    <InputTextarea
                                        value={orderReason}
                                        onChange={(event) => setOrderReason(event.target.value)}
                                        rows={2}
                                        className="w-full"
                                        placeholder="Ejemplo: iniciar por esta ubicación por horario de acceso."
                                    />
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <Button
                                            label="Guardar orden y recalcular"
                                            icon="pi pi-save"
                                            onClick={saveOrder}
                                            loading={loading}
                                        />
                                        <Button
                                            label="Cancelar"
                                            className="p-button-outlined p-button-secondary"
                                            onClick={cancelOrderChanges}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            )}
                        </Card>
                    </div>

                    <div id="route-history" hidden={section !== 'route-history'} role="region" aria-label="Historial" className="col-12 p-2">
                        <Card title="Historial y aprobación">
                            {selected.failure && <p role="status">{selected.failure.message}</p>}
                            {selected.verification && <SubmissionVerification verification={selected.verification} />}
                            <Button label="Consultar versiones anteriores" className="p-button-text" onClick={loadHistory} disabled={loading} />
                            {history && history.length === 0 && <p>No hay versiones anteriores registradas.</p>}
                            {(history || []).map((revision) => <details key={revision._id} className="my-3">
                                <summary>Versión {revision.version} · {revision.route_name} · {revision.service_date}</summary>
                                <DataTable value={revision.items || []} responsiveLayout="scroll">
                                    <Column field="machine_id" header="ID" /><Column field="machine_name" header="Máquina" />
                                    <Column header="Incluida" body={(row) => row.selected ? 'Sí' : 'No'} />
                                    <Column field="action_name" header="Acción" /><Column field="adjustment_reason" header="Motivo" />
                                    <Column field="route_order" header="Orden" />
                                </DataTable>
                            </details>)}
                            <RouteHistory audit={selected.audit || []} token={context.token} currentUser={context.user} active={section === 'route-history'} />
                        </Card>
                    </div>
                </>
            )}
            {selected && <div id="route-follow-up" hidden={section !== 'route-follow-up'} role="region" aria-label="Seguimiento">
                <RouteFollowUp key={selected._id + ":" + selected.version} proposal={selected} token={context.token} active={section === 'route-follow-up'} canWrite={capabilities?.follow_up ?? defaultWrite} onChange={updated => {
                    setSelected(updated); setProposals(current => current.map(proposal => proposal._id === updated._id ? updated : proposal));
                }}><details><summary>Prueba de ubicación en vivo</summary><LiveLocationPilot token={context.token} /></details></RouteFollowUp>
            </div>}
        </PlanningWorkspace>
    );
}
